import { useState } from 'react';
import type {
  ApiKeyModelMappingProvenance,
  KeyPresetActivationDisposition,
  PreviewKeyPresetActivationResponse,
  ApplyKeyPresetActivationResponse,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { createKeyPresetOperationId } from '../api/keyPresetActivations.ts';
import {
  useApplyKeyPresetActivation,
  usePreviewKeyPresetActivation,
  useRollbackKeyPresetActivation,
} from '../api/useKeyPresetActivations.ts';
import { coerceErrorMessage, useToast } from './Toast.tsx';
import { formatLocalTimestamp } from './formatters.ts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  ConfirmDialog,
  Drawer,
  FormField,
  Input,
} from '../ui/index.ts';

interface Props {
  readonly open: boolean;
  readonly skuId: string;
  readonly skuName: string;
  readonly alias: string;
  readonly presetId: string;
  readonly namespaceSlug: string;
  readonly namespaceDisplayName: string;
  readonly onClose: () => void;
}

const DISPOSITION_COPY: Record<KeyPresetActivationDisposition, I18nKey> = {
  ready: 'key_preset_activation.disposition.ready',
  ready_to_subscribe: 'key_preset_activation.disposition.ready_to_subscribe',
  already_managed: 'key_preset_activation.disposition.already_managed',
  not_subscribed: 'key_preset_activation.disposition.not_subscribed',
  not_callable: 'key_preset_activation.disposition.not_callable',
  tombstoned: 'key_preset_activation.disposition.tombstoned',
  user_override: 'key_preset_activation.disposition.user_override',
  managed_conflict: 'key_preset_activation.disposition.managed_conflict',
};

const DISPOSITION_LABEL: Record<KeyPresetActivationDisposition, I18nKey> = {
  ready: 'key_preset_activation.disposition_label.ready',
  ready_to_subscribe: 'key_preset_activation.disposition_label.ready_to_subscribe',
  already_managed: 'key_preset_activation.disposition_label.already_managed',
  not_subscribed: 'key_preset_activation.disposition_label.not_subscribed',
  not_callable: 'key_preset_activation.disposition_label.not_callable',
  tombstoned: 'key_preset_activation.disposition_label.tombstoned',
  user_override: 'key_preset_activation.disposition_label.user_override',
  managed_conflict: 'key_preset_activation.disposition_label.managed_conflict',
};

type OperationResult = ApplyKeyPresetActivationResponse['operation']['result'];
type OperationStatus = ApplyKeyPresetActivationResponse['operation']['status'];

const OPERATION_RESULT_LABEL: Record<OperationResult, I18nKey> = {
  applied: 'key_preset_activation.result_label.applied',
  already_managed: 'key_preset_activation.result_label.already_managed',
  subscribed_applied: 'key_preset_activation.result_label.subscribed_applied',
  subscribed_existing: 'key_preset_activation.result_label.subscribed_existing',
};

const OPERATION_STATUS_LABEL: Record<OperationStatus, I18nKey> = {
  applied: 'key_preset_activation.status_label.applied',
  rolled_back: 'key_preset_activation.status_label.rolled_back',
};

const PROVENANCE_LABEL: Record<ApiKeyModelMappingProvenance, I18nKey> = {
  user: 'key_preset_activation.provenance_label.user',
  managed_preset: 'key_preset_activation.provenance_label.managed_preset',
};

function dispositionTone(disposition: KeyPresetActivationDisposition): 'ok' | 'warn' | 'bad' | 'neutral' {
  if (
    disposition === 'ready' ||
    disposition === 'ready_to_subscribe' ||
    disposition === 'already_managed'
  ) return 'ok';
  if (disposition === 'not_callable' || disposition === 'not_subscribed') return 'bad';
  return 'warn';
}

function redactKeyId(keyId: string): string {
  if (keyId.length <= 12) return keyId;
  return `${keyId.slice(0, 8)}…${keyId.slice(-4)}`;
}

function errorText(error: unknown): string {
  return error instanceof OpsApiError ? `${error.code}: ${error.message}` : coerceErrorMessage(error);
}

/**
 * Admin-only scoped preset activation workflow. The owning route is already
 * protected by the App's `admin(...)` boundary, and ops-api independently
 * enforces ROLE_OPS_ADMIN. The drawer deliberately has no browse-all key
 * source or global option: the operator must provide exact org + key IDs and
 * the server preview proves the complete org/key/preset/SKU scope.
 */
export function KeyPresetActivationDrawer({
  open,
  skuId,
  skuName,
  alias,
  presetId,
  namespaceSlug,
  namespaceDisplayName,
  onClose,
}: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const previewM = usePreviewKeyPresetActivation();
  const applyM = useApplyKeyPresetActivation();
  const rollbackM = useRollbackKeyPresetActivation();
  const [orgId, setOrgId] = useState('');
  const [keyId, setKeyId] = useState('');
  const [preview, setPreview] = useState<PreviewKeyPresetActivationResponse | null>(null);
  const [receipt, setReceipt] = useState<ApplyKeyPresetActivationResponse | null>(null);
  const [operationId, setOperationId] = useState(createKeyPresetOperationId);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versionStale, setVersionStale] = useState(false);
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);

  const pending = previewM.isPending || applyM.isPending || rollbackM.isPending;
  const canApply = preview?.disposition === 'ready' ||
    preview?.disposition === 'ready_to_subscribe' ||
    preview?.disposition === 'already_managed';

  function invalidatePreview(): void {
    setPreview(null);
    setReceipt(null);
    setConfirmed(false);
    setVersionStale(false);
    setError(null);
    setOperationId(createKeyPresetOperationId());
  }

  async function handlePreview(): Promise<void> {
    const normalizedOrgId = orgId.trim();
    const normalizedKeyId = keyId.trim();
    if (normalizedOrgId.length === 0 || normalizedKeyId.length === 0) {
      setError(t('key_preset_activation.scope_required'));
      return;
    }
    setError(null);
    setVersionStale(false);
    setPreview(null);
    setReceipt(null);
    setConfirmed(false);
    try {
      const result = await previewM.mutateAsync({
        org_id: normalizedOrgId,
        api_key_id: normalizedKeyId,
        preset_id: presetId,
        sku_id: skuId,
      });
      setOrgId(normalizedOrgId);
      setKeyId(normalizedKeyId);
      setOperationId(createKeyPresetOperationId());
      setPreview(result);
    } catch (cause) {
      setError(errorText(cause));
    }
  }

  async function handleApply(): Promise<void> {
    if (preview === null || !canApply || !confirmed) return;
    setError(null);
    try {
      const result = await applyM.mutateAsync({
        org_id: preview.org_id,
        api_key_id: preview.api_key_id,
        preset_id: preview.preset_id,
        sku_id: preview.sku_id,
        operation_id: operationId,
        expected_version: preview.current_version,
      });
      setReceipt(result);
      setConfirmed(false);
      toast.success(t('key_preset_activation.applied'));
    } catch (cause) {
      if (cause instanceof OpsApiError && cause.code === 'KEY_PRESET_VERSION_CONFLICT') {
        setPreview(null);
        setConfirmed(false);
        setOperationId(createKeyPresetOperationId());
        setVersionStale(true);
        setError(t('key_preset_activation.version_conflict'));
        return;
      }
      setError(errorText(cause));
    }
  }

  async function handleRollback(): Promise<void> {
    if (receipt === null || receipt.operation.status !== 'applied') return;
    setError(null);
    try {
      const result = await rollbackM.mutateAsync(receipt.operation.id);
      setReceipt(result);
      toast.success(
        result.idempotent_replay
          ? t('key_preset_activation.rollback_replayed')
          : t('key_preset_activation.rolled_back'),
      );
    } catch (cause) {
      setError(errorText(cause));
    }
  }

  return (
    <>
      <Drawer
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onClose();
      }}
      width="xl"
      title={t('key_preset_activation.title')}
      description={t('key_preset_activation.description')}
      closeLabel={t('key_preset_activation.close')}
      testId="key-preset-activation-drawer"
      footer={
        <Button variant="ghost" disabled={pending} onClick={onClose}>
          {t('key_preset_activation.close')}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <section
          className="rounded-lg border border-line-soft bg-well/40 p-4"
          aria-labelledby="key-preset-fixed-scope-title"
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 id="key-preset-fixed-scope-title" className="m-0 text-section text-paper">
                {t('key_preset_activation.fixed_scope')}
              </h3>
              <p className="m-0 mt-1 text-label text-paper-low">
                {t('key_preset_activation.fixed_scope_hint')}
              </p>
            </div>
            <Chip tone="signal" lamp={false}>{t('key_preset_activation.exact_only')}</Chip>
          </div>
          <dl className="m-0 mt-3 grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-label">
            <dt className="text-paper-low">SKU</dt>
            <dd className="m-0 break-all font-mono text-paper">{skuName}</dd>
            <dt className="text-paper-low">SKU ID</dt>
            <dd className="m-0 break-all font-mono text-paper">{skuId}</dd>
            <dt className="text-paper-low">{t('key_preset_activation.namespace')}</dt>
            <dd className="m-0 flex min-w-0 flex-col" data-testid="key-preset-namespace">
              <span className="text-body text-paper">{namespaceDisplayName}</span>
              <span className="break-all font-mono text-micro text-paper-low">{namespaceSlug}</span>
            </dd>
            <dt className="text-paper-low">{t('key_preset_activation.preset_id')}</dt>
            <dd className="m-0 break-all font-mono text-paper-mid" data-testid="key-preset-preset-id">
              {presetId}
            </dd>
            <dt className="text-paper-low">{t('key_preset_activation.alias')}</dt>
            <dd className="m-0 break-all font-mono text-paper">{alias}</dd>
          </dl>
        </section>

        <div className="grid gap-4 md:grid-cols-2">
          <FormField label={t('key_preset_activation.org_id')} fieldId="key-preset-org-id" required>
            <Input
              id="key-preset-org-id"
              mono
              value={orgId}
              disabled={pending}
              onChange={(event) => {
                setOrgId(event.currentTarget.value);
                invalidatePreview();
              }}
              data-testid="key-preset-org-id"
            />
          </FormField>
          <FormField
            label={t('key_preset_activation.key_id')}
            fieldId="key-preset-key-id"
            required
            hint={t('key_preset_activation.key_id_hint')}
          >
            <Input
              id="key-preset-key-id"
              mono
              value={keyId}
              disabled={pending}
              onChange={(event) => {
                setKeyId(event.currentTarget.value);
                invalidatePreview();
              }}
              data-testid="key-preset-key-id"
            />
          </FormField>
        </div>

        <div className="flex justify-end">
          <Button
            variant="primary"
            loading={previewM.isPending}
            disabled={pending || orgId.trim().length === 0 || keyId.trim().length === 0}
            onClick={() => {
              void handlePreview();
            }}
            data-testid="key-preset-preview"
          >
            {t('key_preset_activation.preview')}
          </Button>
        </div>

        {versionStale ? (
          <p role="status" className="m-0 rounded border border-warn-line bg-warn-dim/40 px-3 py-2 text-dense text-warn">
            {t('key_preset_activation.repreview_required')}
          </p>
        ) : null}

        {preview !== null ? (
          <section
            className="rounded-lg border border-line bg-raised p-4"
            aria-labelledby="key-preset-preview-title"
            data-testid="key-preset-preview-result"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 id="key-preset-preview-title" className="m-0 text-section text-paper">
                  {t('key_preset_activation.preview_result')}
                </h3>
                <p className="m-0 mt-1 text-label text-paper-low">
                  {t('key_preset_activation.preview_key', { key: redactKeyId(preview.api_key_id) })}
                </p>
              </div>
              <Chip tone={dispositionTone(preview.disposition)} testId="key-preset-disposition">
                {t(DISPOSITION_LABEL[preview.disposition])}
              </Chip>
            </div>
            <p className="m-0 mt-3 text-dense text-paper-mid">
              {t(DISPOSITION_COPY[preview.disposition])}
            </p>
            <dl className="m-0 mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-label">
              <dt className="text-paper-low">{t('key_preset_activation.applied_version')}</dt>
              <dd className="m-0 text-right font-mono text-paper">
                {preview.last_reconciled_version ?? '—'}
              </dd>
              <dt className="text-paper-low">{t('key_preset_activation.current_version')}</dt>
              <dd className="m-0 text-right font-mono text-paper">{preview.current_version}</dd>
              <dt className="text-paper-low">{t('key_preset_activation.existing_mapping')}</dt>
              <dd className="m-0 text-right font-mono text-paper">
                {preview.existing_mapping === null
                  ? '—'
                  : `${t(PROVENANCE_LABEL[preview.existing_mapping.provenance])} · ${preview.existing_mapping.sku_id}`}
              </dd>
              <dt className="text-paper-low">{t('key_preset_activation.unresolved_aliases')}</dt>
              <dd className="m-0 text-right font-mono text-paper">{preview.unresolved_alias_count}</dd>
            </dl>
            {preview.inferred_tombstone_present ? (
              <p className="m-0 mt-3 rounded border border-warn-line bg-warn-dim/30 px-3 py-2 text-label text-warn">
                {t('key_preset_activation.inferred_tombstone')}
              </p>
            ) : null}
            {canApply && receipt === null ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-line-soft pt-4">
                <Checkbox
                  checked={confirmed}
                  onCheckedChange={setConfirmed}
                  disabled={applyM.isPending}
                  label={t(
                    preview.disposition === 'ready_to_subscribe'
                      ? 'key_preset_activation.confirm_scope_subscribe'
                      : 'key_preset_activation.confirm_scope',
                    {
                    key: redactKeyId(preview.api_key_id),
                    alias: preview.alias ?? alias,
                    },
                  )}
                  testId="key-preset-confirm-scope"
                />
                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    loading={applyM.isPending}
                    disabled={!confirmed || pending}
                    onClick={() => {
                      void handleApply();
                    }}
                    data-testid="key-preset-apply"
                  >
                    {t('key_preset_activation.apply')}
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {receipt !== null ? (
          <section
            className={receipt.operation.status === 'applied'
              ? 'rounded-lg border border-ok-line bg-ok-dim/30 p-4'
              : 'rounded-lg border border-line bg-well/40 p-4'}
            aria-labelledby="key-preset-receipt-title"
            data-testid="key-preset-receipt"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 id="key-preset-receipt-title" className="m-0 text-section text-paper">
                  {t('key_preset_activation.receipt')}
                </h3>
                <p className="m-0 mt-1 text-label text-paper-low">
                  {receipt.idempotent_replay
                    ? t('key_preset_activation.idempotent_replay')
                    : t('key_preset_activation.recorded')}
                </p>
              </div>
              <Chip
                tone={receipt.operation.status === 'applied' ? 'ok' : 'neutral'}
                testId="key-preset-operation-status"
              >
                {t(OPERATION_STATUS_LABEL[receipt.operation.status])}
              </Chip>
            </div>
            <dl className="m-0 mt-3 grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2 text-label">
              <dt className="text-paper-low">{t('key_preset_activation.operation_id')}</dt>
              <dd className="m-0 break-all font-mono text-paper">{receipt.operation.id}</dd>
              <dt className="text-paper-low">{t('key_preset_activation.result')}</dt>
              <dd className="m-0 text-paper" data-testid="key-preset-operation-result">
                {t(OPERATION_RESULT_LABEL[receipt.operation.result])}
              </dd>
              <dt className="text-paper-low">{t('key_preset_activation.preset_version')}</dt>
              <dd className="m-0 font-mono text-paper">{receipt.operation.preset_version}</dd>
              <dt className="text-paper-low">{t('key_preset_activation.key_id')}</dt>
              <dd className="m-0 font-mono text-paper">{redactKeyId(receipt.operation.api_key_id)}</dd>
              <dt className="text-paper-low">{t('key_preset_activation.alias')}</dt>
              <dd className="m-0 font-mono text-paper">{receipt.operation.alias}</dd>
              <dt className="text-paper-low">{t('key_preset_activation.created_at')}</dt>
              <dd className="m-0 font-mono text-paper">{formatLocalTimestamp(receipt.operation.created_at)}</dd>
              <dt className="text-paper-low">{t('key_preset_activation.cache_convergence')}</dt>
              <dd className="m-0" data-testid="key-preset-cache-convergence">
                <Chip tone={receipt.cache.convergence === 'immediate' ? 'ok' : 'warn'} lamp={false}>
                  {t(
                    receipt.cache.convergence === 'immediate'
                      ? 'key_preset_activation.cache_immediate'
                      : 'key_preset_activation.cache_degraded',
                  )}
                </Chip>
              </dd>
              <dt className="text-paper-low">{t('key_preset_activation.cache_effective_after')}</dt>
              <dd className="m-0 font-mono text-paper" data-testid="key-preset-cache-effective-after">
                {formatLocalTimestamp(receipt.cache.effective_after)}
              </dd>
              <dt className="text-paper-low">{t('key_preset_activation.cache_revision')}</dt>
              <dd className="m-0 break-all font-mono text-paper-low">
                {receipt.cache.revision ?? '—'}
              </dd>
              {receipt.operation.rolled_back_at !== null ? (
                <>
                  <dt className="text-paper-low">{t('key_preset_activation.rolled_back_at')}</dt>
                  <dd className="m-0 font-mono text-paper">
                    {formatLocalTimestamp(receipt.operation.rolled_back_at)}
                  </dd>
                </>
              ) : null}
            </dl>
            {receipt.operation.status === 'applied' ? (
              <div className="mt-4 flex items-center justify-between gap-3 border-t border-ok-line pt-4">
                <p className="m-0 text-label text-paper-low">
                  {t(
                    receipt.operation.result === 'subscribed_existing'
                      ? 'key_preset_activation.rollback_hint_subscribed_existing'
                      : receipt.operation.result === 'subscribed_applied'
                        ? 'key_preset_activation.rollback_hint_subscribed_applied'
                        : receipt.operation.result === 'already_managed'
                          ? 'key_preset_activation.rollback_hint_already_managed'
                          : 'key_preset_activation.rollback_hint',
                  )}
                </p>
                <Button
                  variant="danger"
                  loading={rollbackM.isPending}
                  disabled={pending}
                  onClick={() => setRollbackConfirmOpen(true)}
                  data-testid="key-preset-rollback"
                >
                  {t('key_preset_activation.rollback')}
                </Button>
              </div>
            ) : null}
          </section>
        ) : null}

        {error !== null ? (
          <p role="alert" className="m-0 rounded border border-bad-line bg-bad-dim/40 px-3 py-2 text-dense text-bad-bright" data-testid="key-preset-error">
            {error}
          </p>
        ) : null}

        <p className="m-0 rounded border border-line-soft bg-well/30 px-3 py-2 text-label text-paper-low">
          {t('key_preset_activation.non_effects')}
        </p>
      </div>
      </Drawer>

      {rollbackConfirmOpen && receipt !== null ? (
        <ConfirmDialog
          open
          tone="danger"
          onOpenChange={(next) => {
            if (!next && !rollbackM.isPending) setRollbackConfirmOpen(false);
          }}
          title={t('key_preset_activation.rollback_confirm_title')}
          body={
            receipt.operation.result === 'already_managed'
              ? t('key_preset_activation.rollback_confirm_already_managed')
              : receipt.operation.result === 'subscribed_existing'
                ? t('key_preset_activation.rollback_confirm_subscribed_existing')
                : receipt.operation.result === 'subscribed_applied'
                  ? t('key_preset_activation.rollback_confirm_subscribed_applied')
                  : t('key_preset_activation.rollback_confirm_applied')
          }
          confirmLabel={t('key_preset_activation.rollback_confirm')}
          cancelLabel={t('key_preset_activation.rollback_cancel')}
          closeLabel={t('key_preset_activation.close')}
          loading={rollbackM.isPending}
          testId="key-preset-rollback-confirm"
          onConfirm={() => {
            setRollbackConfirmOpen(false);
            void handleRollback();
          }}
        />
      ) : null}
    </>
  );
}
